import type { UnderstandResponse } from '@/types/content'
import type { GuestLocale } from '@/lib/guestLocale'

export const LANDING_DEMO_PARAGRAPH =
  'She felt nervous before her first presentation. Her hands were shaking, but she took a deep breath and walked onto the stage.'

type DemoWordLocaleCopy = {
  translation: string
  gloss: string
  inContext: string
  partOfSpeech: string
}

const DEMO_WORD_LOCALES: Record<
  GuestLocale,
  Record<'nervous' | 'presentation', DemoWordLocaleCopy>
> = {
  en: {
    nervous: {
      translation: 'worried, anxious',
      gloss: 'Feeling worried or afraid about something.',
      inContext: 'She was worried and tense before speaking in public.',
      partOfSpeech: 'adjective',
    },
    presentation: {
      translation: 'a formal talk to an audience',
      gloss: 'A formal talk given to an audience.',
      inContext: 'Her first formal talk in front of an audience.',
      partOfSpeech: 'noun',
    },
  },
  ru: {
    nervous: {
      translation: 'тревожный, взволнованный',
      gloss: 'Ощущение беспокойства или страха перед чем-то.',
      inContext: 'Она волновалась и была напряжена перед выступлением на публике.',
      partOfSpeech: 'прилагательное',
    },
    presentation: {
      translation: 'выступление, презентация',
      gloss: 'Официальный доклад или выступление перед аудиторией.',
      inContext: 'Её первое официальное выступление перед аудиторией.',
      partOfSpeech: 'существительное',
    },
  },
  uk: {
    nervous: {
      translation: 'тривожний, схвильований',
      gloss: 'Відчуття занепокоєння або страху перед чимось.',
      inContext: 'Вона хвилювалася і була напружена перед виступом публічно.',
      partOfSpeech: 'прикметник',
    },
    presentation: {
      translation: 'виступ, презентація',
      gloss: 'Офіційна доповідь або виступ перед аудиторією.',
      inContext: 'Її перший офіційний виступ перед аудиторією.',
      partOfSpeech: 'іменник',
    },
  },
  pl: {
    nervous: {
      translation: 'nerwowy, zaniepokojony',
      gloss: 'Poczucie niepokoju lub strachu przed czymś.',
      inContext: 'Była spięta i zdenerwowana przed wystąpieniem publicznym.',
      partOfSpeech: 'przymiotnik',
    },
    presentation: {
      translation: 'prezentacja, wystąpienie',
      gloss: 'Oficjalne wystąpienie lub referat przed publicznością.',
      inContext: 'Jej pierwsze oficjalne wystąpienie przed publicznością.',
      partOfSpeech: 'rzeczownik',
    },
  },
}

const DEMO_META = {
  nervous: {
    phonetic: '/ˈnɜː.vəs/',
    senseKey: 'adj:1:nervous',
    example: 'She felt nervous before the exam.',
    definitionEn: 'Feeling worried or afraid about something.',
  },
  presentation: {
    phonetic: '/ˌprez.enˈteɪ.ʃən/',
    senseKey: 'n:1:presentation',
    example: 'She gave a short presentation to the team.',
    definitionEn: 'A formal talk given to an audience.',
  },
} as const

type DemoWord = keyof typeof DEMO_META

function buildDemoResponse(word: DemoWord, locale: GuestLocale): UnderstandResponse {
  const meta = DEMO_META[word]
  const local = DEMO_WORD_LOCALES[locale][word]
  const sense = {
    senseKey: meta.senseKey,
    partOfSpeech: local.partOfSpeech,
    translation: local.translation,
    nativeGloss: local.gloss,
    englishGloss: local.gloss,
    cefrLevel: 'B1' as const,
    isPrimary: true,
  }

  return {
    kind: 'WORD',
    selection: word,
    examples: [],
    wordDetails: {
      word,
      phonetic: meta.phonetic,
      cefrLevel: 'B1',
      nativeLanguage: locale === 'en' ? 'en' : locale,
      primaryTranslation: local.translation,
      localizedDefinitions: [sense],
      translationLocalized: true,
      definitions: [
        {
          partOfSpeech: local.partOfSpeech,
          definition: meta.definitionEn,
          isPrimary: true,
        },
      ],
      examples: [meta.example],
      inVocabulary: false,
      enrichmentStatus: 'COMPLETE',
      learnedSenses: [],
    },
  }
}

export const LANDING_DEMO_DEFAULT_WORD = 'nervous'

export function landingDemoResponseFor(
  word: string,
  locale: GuestLocale = 'en',
): UnderstandResponse | null {
  const lower = word.toLowerCase()
  if (lower !== 'nervous' && lower !== 'presentation') return null
  return buildDemoResponse(lower, locale)
}

export function landingDemoContextFor(
  word: string,
  locale: GuestLocale = 'en',
): string | null {
  const lower = word.toLowerCase()
  if (lower !== 'nervous' && lower !== 'presentation') return null
  return DEMO_WORD_LOCALES[locale][lower].inContext
}

export function landingDemoSentenceFor(word: string): string | null {
  const lower = word.toLowerCase()
  const idx = LANDING_DEMO_PARAGRAPH.toLowerCase().indexOf(lower)
  if (idx < 0) return null

  let start = idx
  while (start > 0 && !/[.!?]/.test(LANDING_DEMO_PARAGRAPH[start - 1])) start--
  if (start > 0) start++
  while (start < LANDING_DEMO_PARAGRAPH.length && /\s/.test(LANDING_DEMO_PARAGRAPH[start])) start++

  let end = idx + lower.length
  while (end < LANDING_DEMO_PARAGRAPH.length && !/[.!?]/.test(LANDING_DEMO_PARAGRAPH[end])) end++
  if (end < LANDING_DEMO_PARAGRAPH.length) end++

  return LANDING_DEMO_PARAGRAPH.slice(start, end).trim()
}
